import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ref, get, onValue } from 'firebase/database';
import { database } from '../firebase';
import { useAccessControl } from '../hooks/useAccessControl';
import useTeamAccessProfile from '../hooks/useTeamAccessProfile';
import SubscriptionPopup from './applicants/SubscriptionPopup';

import SidebarNavigation from './Navigation/SidebarNavigation';
import { isSourcingOnly } from '../utils/sidebarPreferences';

const getCommunitySidebarVisibilityCacheKey = (userId = '') => {
  const normalizedUserId = String(userId || '').trim();
  return normalizedUserId ? `sidebar-community-visible:${normalizedUserId}` : '';
};

const readCommunitySidebarVisibilityCache = (userId = '') => {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return null;
  }

  const cacheKey = getCommunitySidebarVisibilityCacheKey(userId);
  if (!cacheKey) {
    return null;
  }

  try {
    const rawValue = window.sessionStorage.getItem(cacheKey);
    return rawValue === 'true' ? true : rawValue === 'false' ? false : null;
  } catch (error) {
    console.error('Error reading community sidebar cache:', error);
    return null;
  }
};

const writeCommunitySidebarVisibilityCache = (userId = '', visible = null) => {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return;
  }

  const cacheKey = getCommunitySidebarVisibilityCacheKey(userId);
  if (!cacheKey) {
    return;
  }

  try {
    if (typeof visible !== 'boolean') {
      window.sessionStorage.removeItem(cacheKey);
      return;
    }

    window.sessionStorage.setItem(cacheKey, visible ? 'true' : 'false');
  } catch (error) {
    console.error('Error writing community sidebar cache:', error);
  }
};

const getManpowerSidebarVisibilityCacheKey = (userId = '') => {
  const normalizedUserId = String(userId || '').trim();
  return normalizedUserId ? `sidebar-manpower-visible:${normalizedUserId}` : '';
};

const readManpowerSidebarVisibilityCache = (userId = '') => {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return null;
  }

  const cacheKey = getManpowerSidebarVisibilityCacheKey(userId);
  if (!cacheKey) {
    return null;
  }

  try {
    const rawValue = window.sessionStorage.getItem(cacheKey);
    return rawValue === 'true' ? true : rawValue === 'false' ? false : null;
  } catch (error) {
    console.error('Error reading manpower sidebar cache:', error);
    return null;
  }
};

const writeManpowerSidebarVisibilityCache = (userId = '', visible = null) => {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return;
  }

  const cacheKey = getManpowerSidebarVisibilityCacheKey(userId);
  if (!cacheKey) {
    return;
  }

  try {
    if (typeof visible !== 'boolean') {
      window.sessionStorage.removeItem(cacheKey);
      return;
    }

    window.sessionStorage.setItem(cacheKey, visible ? 'true' : 'false');
  } catch (error) {
    console.error('Error writing manpower sidebar cache:', error);
  }
};

const Sidebar = ({ collapsed: collapsedProp, onToggle }) => {
  const [collapsed, setCollapsed] = useState(!!collapsedProp);
  const { currentUser, logout } = useAuth();
  const [companyName, setCompanyName] = useState(() => {
    try {
      return localStorage.getItem('companyName') || '';
    } catch (e) {
      return '';
    }
  });
  const [showCommunityItem, setShowCommunityItem] = useState(() => readCommunitySidebarVisibilityCache(currentUser?.uid));
  const [manpowerRequestsEnabled, setManpowerRequestsEnabled] = useState(() => readManpowerSidebarVisibilityCache(currentUser?.uid));
  const location = useLocation();
  const navigate = useNavigate();
  const teamAccess = useTeamAccessProfile();

  // ✅ Check for trial status and product access
  const { accessType, loading: accessLoading, trial, productTypes } = useAccessControl();

  // ✅ Subscription popup state
  const [showSubscriptionPopup, setShowSubscriptionPopup] = useState(false);

  // ✅ Check if user has ATS access
  const hasATSAccess = productTypes.includes('ats') || productTypes.includes('bundle');
  useEffect(() => {
    setCollapsed(!!collapsedProp);
  }, [collapsedProp]);

  useEffect(() => {
    setShowCommunityItem(readCommunitySidebarVisibilityCache(currentUser?.uid));
  }, [currentUser?.uid]);

  useEffect(() => {
    setManpowerRequestsEnabled(readManpowerSidebarVisibilityCache(currentUser?.uid));
  }, [currentUser?.uid]);

  useEffect(() => {
    if (!currentUser) {
      setShowCommunityItem(false);
      return;
    }

    if (teamAccess.companyFeatures?.communityEnabled === false) {
      setShowCommunityItem(false);
      writeCommunitySidebarVisibilityCache(currentUser.uid, false);
      return;
    }

    if (teamAccess.companyFeatures?.communityEnabled === true && !accessLoading) {
      const nextValue = hasATSAccess;
      setShowCommunityItem(nextValue);
      writeCommunitySidebarVisibilityCache(currentUser.uid, nextValue);
    }
  }, [accessLoading, currentUser, hasATSAccess, teamAccess.companyFeatures?.communityEnabled]);

  // Keep a global CSS variable for sidebar width so pages can align their content
  useEffect(() => {
    const width = collapsed ? '60px' : '240px';
    document.documentElement.style.setProperty('--sidebar-width', width);
  }, [collapsed]);

  // Load company name compatible with owner and collaborator accounts
  useEffect(() => {
    const loadName = async () => {
      try {
        if (!currentUser) return;
        // Check if user is owner
        const ownerSnap = await get(ref(database, `companies/${currentUser.uid}`));
        let companyId = null;
        if (ownerSnap.exists()) {
          companyId = currentUser.uid;
        } else {
          const collabSnap = await get(ref(database, `collaborators/${currentUser.uid}`));
          if (collabSnap.exists()) {
            const collaboratorData = collabSnap.val() || {};
            companyId = collaboratorData.companyId || collaboratorData.company_uid;
          }
        }
        if (companyId) {
          const companySnap = await get(ref(database, `companies/${companyId}`));
          if (companySnap.exists()) {
            const data = companySnap.val();
            const name = data.companyName || 'Company';
            setCompanyName(name);
            localStorage.setItem('companyName', name);
          }
        }
      } catch (e) {
        console.error('Failed loading company name:', e);
      }
    };
    loadName();
  }, [currentUser]);

  const isActive = (path) => location.pathname === path;
  const isManpowerRequestsRoute = isActive('/manpower-requests');
  const canManageManpowerRequests = teamAccess.hasPermission('canPostJobs');
  const showManpowerRequestsItem = isManpowerRequestsRoute
    || (manpowerRequestsEnabled === true && (teamAccess.loading || canManageManpowerRequests));

  const postAJob = (e) => {
    e.preventDefault();
    // Navigate to home and request opening the modal via state
    navigate('/', { state: { openPostJob: true } });
  };

  useEffect(() => {
    if (!teamAccess.companyUID) {
      return undefined;
    }

    const settingsRef = ref(database, `companies/${teamAccess.companyUID}/settings/manpowerRequest/enabled`);
    const unsubscribe = onValue(settingsRef, (snapshot) => {
      const nextValue = snapshot.val() === true;
      setManpowerRequestsEnabled(nextValue);
      writeManpowerSidebarVisibilityCache(currentUser?.uid, nextValue);
    }, (error) => {
      console.error('Failed loading manpower request sidebar setting:', error);
    });

    return unsubscribe;
  }, [currentUser?.uid, teamAccess.companyUID]);

  const handleSignOut = async (e) => {
    e.preventDefault();
    try {
      await logout();
      navigate('/login');
    } catch (err) {
      console.error('Sign out failed:', err);
    }
  };

  return <>
    <SidebarNavigation companyName={companyName} collapsed={collapsed} onToggle={next => {
      setCollapsed(next); onToggle?.(next);
    }} sourcingOnly={isSourcingOnly(productTypes)} onPostJob={postAJob}
      onSignOut={handleSignOut} onUpgrade={() => setShowSubscriptionPopup(true)}
      trial={accessType === 'trial' ? trial : null} showCommunityItem={showCommunityItem}
      showManpowerRequestsItem={showManpowerRequestsItem} />
    {showSubscriptionPopup && <SubscriptionPopup isOpen onClose={() => setShowSubscriptionPopup(false)} userUID={currentUser?.uid} />}
  </>;
};

export default Sidebar;
